/* ------------------------------------------------------------------
 * PDP Delivery Estimate — enhances the stock status line with
 * delivery date info from JS Rates + expandable carrier detail
 * with cut-off times.
 *
 * Three-tier postcode resolution:
 *   1. customer.default_address.zip (Liquid-rendered data attribute)
 *   2. localStorage (persists across sessions, shared with delivery-page widget)
 *   3. Inline input (first-time guest)
 *
 * Shares cache keys with shipping-estimator.js (delivery page widget)
 * so a postcode entered on either page carries over.
 * ------------------------------------------------------------------ */
(function () {
  'use strict';

  var CACHE_PREFIX = 'se_rates_';
  var POSTCODE_KEY = 'se_postcode';
  var CACHE_MAX_AGE = 4 * 60 * 60 * 1000; // 4 hours — stale after cut-off
  var EXCLUDED = ['Store at Different Drop and Deliver Later'];
  var ORIGIN = {
    country: 'AU', postal_code: '2007', province: 'NSW',
    city: 'Ultimo', address1: '3 Fig St', company_name: 'Different Drop'
  };
  var REP_ITEM = { quantity: 1, grams: 1500, price: 3000, requires_shipping: true };

  // Cut-off times per carrier (business rules — not returned by JS Rates API).
  // `days` = which days the cut-off creates genuine urgency (ordering before
  // vs after changes the delivery date). DD Local skips Fri because weekend
  // orders all dispatch Mon regardless.
  var CUTOFFS = {
    'Different Drop Local Delivery': { time: '11:59pm', days: ['Mon', 'Tue', 'Wed', 'Thu'] },
    'Australia Post':                { time: '11:00am', days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'] },
    'Anonymous Wine Freight':        { time: '12:01am', days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'] },
    'StarTrack Evening':             { time: '1:00pm',  days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'] }
  };

  var activeController = null;

  // ---- Storage helpers (localStorage, shared with delivery-page widget) ----

  function getSavedPostcode() {
    var el = document.querySelector('[data-saved-postcode]');
    if (el) {
      var pc = el.getAttribute('data-saved-postcode').trim();
      if (/^\d{4}$/.test(pc)) return pc;
    }
    try { var v = localStorage.getItem(POSTCODE_KEY); if (v && /^\d{4}$/.test(v)) return v; } catch (e) {}
    return null;
  }

  function savePostcode(pc) {
    try { localStorage.setItem(POSTCODE_KEY, pc); } catch (e) {}
  }

  function getCachedRates(pc) {
    try {
      var raw = localStorage.getItem(CACHE_PREFIX + pc);
      if (!raw) return null;
      var entry = JSON.parse(raw);
      if (!entry || !entry.ts) return null;
      if (Date.now() - entry.ts > CACHE_MAX_AGE) return null;
      return entry.rates || null;
    } catch (e) { return null; }
  }

  function cacheRates(pc, rates) {
    try { localStorage.setItem(CACHE_PREFIX + pc, JSON.stringify({ rates: rates, ts: Date.now() })); } catch (e) {}
  }

  // ---- API ----

  function fetchRates(postcode) {
    if (activeController) activeController.abort();
    activeController = new AbortController();
    return fetch('/apps/jsrates/rates', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rate: { origin: ORIGIN, destination: { country: 'AU', postal_code: postcode }, items: [REP_ITEM], currency: 'AUD', locale: 'en' } }),
      signal: activeController.signal
    })
    .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
    .then(function (d) {
      var rates = (d && d.rates) ? d.rates : [];
      return rates.filter(function (r) {
        var n = (r.service_name || '').trim();
        return n && EXCLUDED.indexOf(n) === -1;
      });
    });
  }

  // ---- DOM helpers ----

  function escHtml(s) { var d = document.createElement('div'); d.textContent = s; return d.innerHTML; }

  function parseStockState(el) {
    var text = el.textContent || '';
    if (/re-?stocking|sold out|delayed dispatch|incoming/i.test(text)) return 'unavailable';
    if (/only\s+\d+\s+unit/i.test(text)) return 'low';
    if (/in stock/i.test(text)) return 'instock';
    return 'unknown';
  }

  function extractLowCount(el) {
    var m = (el.textContent || '').match(/only\s+(\d+)\s+unit/i);
    return m ? m[1] : null;
  }

  function bestRate(rates) {
    return rates.length ? rates[0] : null;
  }

  function buildStockPrefix(state, el) {
    if (state === 'low') {
      var count = extractLowCount(el);
      return count ? 'In stock, only ' + count + ' left' : 'In stock';
    }
    return 'In stock';
  }

  // ---- Cut-off countdown helpers ----

  // Get current Sydney time components
  function getSydneyTime() {
    try {
      var now = new Date();
      var parts = new Intl.DateTimeFormat('en-AU', {
        timeZone: 'Australia/Sydney',
        hour: 'numeric', minute: 'numeric', hour12: false, weekday: 'short'
      }).formatToParts(now);
      var h = 0, m = 0, wd = '';
      parts.forEach(function (p) {
        if (p.type === 'hour') h = parseInt(p.value, 10);
        if (p.type === 'minute') m = parseInt(p.value, 10);
        if (p.type === 'weekday') wd = p.value;
      });
      return { hour: h, minute: m, weekday: wd };
    } catch (e) { return null; }
  }

  // Parse "11:59pm" → { hour: 23, minute: 59 }
  function parseCutoffTime(str) {
    var match = str.match(/(\d+):(\d+)\s*(am|pm)/i);
    if (!match) return null;
    var h = parseInt(match[1], 10);
    var min = parseInt(match[2], 10);
    var ap = match[3].toLowerCase();
    if (ap === 'pm' && h !== 12) h += 12;
    if (ap === 'am' && h === 12) h = 0;
    return { hour: h, minute: min };
  }

  // Returns a countdown string ("3h 22m") if the cut-off is still achievable
  // today (must be one of the carrier's valid days AND before the cut-off in AEST).
  // Returns null otherwise — meaning no urgency to communicate.
  function getCountdown(cutoffEntry) {
    if (!cutoffEntry) return null;
    var syd = getSydneyTime();
    if (!syd) return null;
    // Check if today is one of the carrier's valid cut-off days
    if (cutoffEntry.days && cutoffEntry.days.indexOf(syd.weekday) === -1) return null;
    var co = parseCutoffTime(cutoffEntry.time);
    if (!co) return null;
    var diff = (co.hour * 60 + co.minute) - (syd.hour * 60 + syd.minute);
    if (diff <= 0) return null; // cut-off already passed today
    var hrs = Math.floor(diff / 60);
    var mins = diff % 60;
    if (hrs > 0) return hrs + 'h ' + mins + 'm';
    return mins + 'm';
  }

  // Strip JS Rates description to a compact date string:
  // "Delivery by Mon, Apr 20, 2026 - between 9am-3pm" → "by Mon, Apr 20"
  function cleanDescription(desc) {
    if (!desc) return '';
    return desc
      .replace(/^Delivery\s*/i, '')
      .replace(/\s*-\s*between\s.*/i, '')
      .replace(/,\s*\d{4}\b/, '')
      .trim();
  }

  // Remove any existing detail panel (sibling of the inventory element)
  function removeDetail(el) {
    var existing = el.parentNode && el.parentNode.querySelector('.pdp-de__detail');
    if (existing) existing.remove();
  }

  // ---- Render states ----

  function renderWithDelivery(el, state, postcode, rates) {
    var rate = bestRate(rates);
    var prefix = buildStockPrefix(state, el);
    var desc = rate ? cleanDescription(rate.description || '') : '';
    var serviceName = rate ? (rate.service_name || '').trim() : '';

    // Build the collapsed summary line
    var deliveryHtml = '';
    var cutoffEntry = serviceName ? (CUTOFFS[serviceName] || null) : null;

    if (desc) {
      deliveryHtml = 'Deliver to <strong>' + escHtml(postcode) + '</strong> ' + escHtml(desc);
    } else if (serviceName) {
      deliveryHtml = 'Deliver to <strong>' + escHtml(postcode) + '</strong> via ' + escHtml(serviceName);
    } else {
      deliveryHtml = 'Deliver to <strong>' + escHtml(postcode) + '</strong>';
    }

    // Show countdown only if cut-off is still achievable today (AEST business day).
    // Otherwise the delivery date from JS Rates already reflects the next dispatch.
    var countdown = cutoffEntry ? getCountdown(cutoffEntry) : null;
    if (countdown) {
      deliveryHtml += ' &middot; <span class="pdp-de__cutoff">order within ' + escHtml(countdown) + '</span>';
    }

    // Only show toggle if there are multiple rates to expand
    var showToggle = rates.length > 1;

    el.innerHTML = escHtml(prefix) + ' &middot; ' + deliveryHtml +
      (showToggle ? ' <button type="button" class="pdp-de__toggle" aria-label="Show all delivery options" aria-expanded="false">&#9662;</button>' : '') +
      ' <button type="button" class="pdp-de__change" aria-label="Change postcode">change</button>';

    el.classList.remove('inventory--low', 'inventory--high');
    el.classList.add(state === 'low' ? 'inventory--low' : 'inventory--high');

    // Build the expandable detail panel as a sibling (survives theme.js innerHTML overwrites)
    removeDetail(el);

    if (showToggle) {
      var detail = document.createElement('div');
      detail.className = 'pdp-de__detail';
      detail.innerHTML = rates.map(function (r) {
        var name = (r.service_name || '').trim();
        var dateStr = cleanDescription(r.description || '') || '—';
        var ce = CUTOFFS[name] || null;
        var cutoffTime = ce ? ce.time : '';
        return '<div class="pdp-de__detail-row">' +
          '<span class="pdp-de__detail-service">' + escHtml(name) + '</span>' +
          '<span class="pdp-de__detail-date">' + escHtml(dateStr) + '</span>' +
          (cutoffTime ? '<span class="pdp-de__detail-cutoff">order by ' + escHtml(cutoffTime) + '</span>' : '') +
          '</div>';
      }).join('');

      // Insert after the inventory element
      el.parentNode.insertBefore(detail, el.nextSibling);
    }
  }

  function renderInput(el, state) {
    var prefix = buildStockPrefix(state, el);
    el.innerHTML = escHtml(prefix) + ' &middot; ' +
      '<input type="text" inputmode="numeric" maxlength="4" placeholder="Postcode" class="pdp-de__input" aria-label="Enter postcode"> ' +
      '<button type="button" class="pdp-de__go">Go</button>';
    el.classList.remove('inventory--low', 'inventory--high');
    el.classList.add(state === 'low' ? 'inventory--low' : 'inventory--high');
    removeDetail(el);
  }

  function renderLoading(el, state, postcode) {
    var prefix = buildStockPrefix(state, el);
    el.innerHTML = escHtml(prefix) + ' &middot; Checking delivery to ' + escHtml(postcode) + '&hellip;';
    removeDetail(el);
  }

  // ---- Core enhance logic ----

  function enhance(el) {
    if (!el) return;

    // Skip non-shippable products (e.g. digital gift cards)
    var infoItem = el.closest('.product-form__info-item');
    if (infoItem && infoItem.dataset.noShipping === 'true') return;

    var state = parseStockState(el);

    if (state !== 'instock' && state !== 'low') return;

    if (!el.dataset.pdpDeOriginal) {
      el.dataset.pdpDeOriginal = el.innerHTML;
    }

    var postcode = getSavedPostcode();

    if (!postcode) {
      renderInput(el, state);
      bindInputEvents(el, state);
      return;
    }

    var cached = getCachedRates(postcode);
    if (cached) {
      renderWithDelivery(el, state, postcode, cached);
      bindEvents(el, state);
      return;
    }

    renderLoading(el, state, postcode);
    fetchRates(postcode)
      .then(function (rates) {
        cacheRates(postcode, rates);
        var currentState = parseStockState(el);
        if (currentState === 'instock' || currentState === 'low') {
          renderWithDelivery(el, currentState, postcode, rates);
          bindEvents(el, currentState);
        }
      })
      .catch(function (err) {
        if (err.name === 'AbortError') return;
        if (el.dataset.pdpDeOriginal) el.innerHTML = el.dataset.pdpDeOriginal;
      });
  }

  // ---- Event binding ----

  function bindEvents(el, state) {
    bindChangeEvent(el, state);
    bindToggleEvent(el);
  }

  function bindToggleEvent(el) {
    var btn = el.querySelector('.pdp-de__toggle');
    if (!btn) return;
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      var detail = el.parentNode && el.parentNode.querySelector('.pdp-de__detail');
      if (!detail) return;
      var isOpen = detail.classList.toggle('is-open');
      btn.innerHTML = isOpen ? '&#9652;' : '&#9662;'; // ▴ / ▾
      btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
  }

  function bindInputEvents(el, state) {
    var input = el.querySelector('.pdp-de__input');
    var btn = el.querySelector('.pdp-de__go');
    if (!input || !btn) return;

    function submit() {
      var pc = input.value.replace(/\s/g, '');
      if (!/^\d{4}$/.test(pc)) {
        input.setCustomValidity('Enter a 4-digit postcode');
        input.reportValidity();
        return;
      }
      input.setCustomValidity('');
      savePostcode(pc);
      renderLoading(el, state, pc);
      fetchRates(pc)
        .then(function (rates) {
          cacheRates(pc, rates);
          renderWithDelivery(el, state, pc, rates);
          bindEvents(el, state);
        })
        .catch(function (err) {
          if (err.name === 'AbortError') return;
          renderInput(el, state);
          bindInputEvents(el, state);
        });
    }

    btn.addEventListener('click', submit);
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); submit(); }
    });
  }

  function bindChangeEvent(el, state) {
    var btn = el.querySelector('.pdp-de__change');
    if (!btn) return;
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      renderInput(el, state);
      bindInputEvents(el, state);
      var input = el.querySelector('.pdp-de__input');
      if (input) input.focus();
    });
  }

  // ---- Init + variant change listener ----

  function init() {
    var el = document.querySelector('.product-form__inventory');
    if (el) enhance(el);
  }

  document.addEventListener('variant:changed', function () {
    setTimeout(function () {
      var el = document.querySelector('.product-form__inventory');
      if (el) {
        removeDetail(el); // clean up sibling detail from previous variant
        el.dataset.pdpDeOriginal = el.innerHTML;
        enhance(el);
      }
    }, 0);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
